<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useTablesStore } from 'dashboard/stores/tables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ColumnManager from '../components/ColumnManager.vue';
import RowDetailPanel from '../components/RowDetailPanel.vue';
import CsvImport from '../components/CsvImport.vue';
import SavedQueryEditor from '../components/SavedQueryEditor.vue';
import GrantManager from '../components/GrantManager.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const tablesStore = useTablesStore();

const tableId = Number(route.params.tableId);
const table = computed(() => tablesStore.getTable(tableId));
const columns = computed(() => tablesStore.columns);
const rows = computed(() => tablesStore.rows);

const activeTab = ref('rows');
const TABS = ['rows', 'columns', 'queries', 'grants', 'import'];

const search = ref('');
const sortBy = ref('');
const sortOrder = ref('asc');
const page = ref(1);
const selectedRow = ref(null);
const errorMessage = ref('');

// Which columns THIS user may write, served by the API from the same resolver
// the backend authorizes with. It must not be derived from the table's grant
// list — that list holds every subject's grants, so a colleague's write access
// would have made cells look editable that the server then refuses.
const permissions = computed(() => table.value?.current_user_permissions || {});
const writableKeys = computed(() => permissions.value.writable_columns || []);
const canCreateRows = computed(() => permissions.value.can_create_rows === true);
const canDeleteRows = computed(() => permissions.value.can_delete_rows === true);

const loadRows = async () => {
  await tablesStore.fetchRows(tableId, {
    search: search.value || undefined,
    sort_by: sortBy.value || undefined,
    sort_order: sortOrder.value,
    page: page.value,
  });
};

const sortColumn = key => {
  if (sortBy.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortBy.value = key;
    sortOrder.value = 'asc';
  }
  loadRows();
};

const runSearch = () => {
  page.value = 1;
  loadRows();
};

const changePage = delta => {
  const next = page.value + delta;
  if (next < 1 || next > tablesStore.totalPages) return;
  page.value = next;
  loadRows();
};

// Opens an unsaved draft rather than persisting an empty row. Creating first
// and editing after cannot work: a table with any `required` column rejects
// the empty row outright, so the user is shown a validation error for fields
// they were never given the chance to fill.
const addRow = () => {
  errorMessage.value = '';
  selectedRow.value = { id: null, data: {} };
};

const removeRow = async row => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TABLES.ROW.CONFIRM_DELETE'))) return;
  errorMessage.value = '';
  try {
    await tablesStore.deleteRow(tableId, row.id);
    if (selectedRow.value?.id === row.id) selectedRow.value = null;
  } catch (error) {
    errorMessage.value = error?.response?.data?.error || t('TABLES.ERROR');
  }
};

const onRowSaved = () => loadRows();

// An import into a table with no columns creates them, so the grid needs
// the new schema, the new rows, and a refreshed permission snapshot (the
// cached one predates the imported columns).
const onImported = async () => {
  await tablesStore.fetchColumns(tableId);
  await tablesStore.fetchTable(tableId);
  await loadRows();
};

const goBack = () =>
  router.push({ name: 'tables_index', params: { accountId: route.params.accountId } });

const displayValue = (row, column) => {
  const value = row.data?.[column.key];
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? '✓' : '—';
  return value;
};

onMounted(async () => {
  // Always refetch: the table payload carries this user's own effective
  // permissions, which the grid reads to decide what is editable.
  await tablesStore.fetch();
  await tablesStore.fetchColumns(tableId);
  await loadRows();
});
</script>

<template>
  <div class="flex w-full h-full overflow-hidden bg-n-surface-1">
    <div class="flex flex-col flex-1 overflow-hidden">
      <div class="flex items-center gap-3 px-6 py-4 border-b border-n-weak flex-shrink-0">
        <Button icon="i-lucide-arrow-left" size="xs" variant="ghost" @click="goBack" />
        <h1 class="m-0 text-lg font-semibold text-n-slate-12">
          {{ table?.name || t('TABLES.TITLE') }}
        </h1>
        <code v-if="table" class="text-xs text-n-slate-11">{{ table.slug }}</code>
      </div>

      <div class="flex gap-1 px-6 pt-3 border-b border-n-weak flex-shrink-0">
        <button
          v-for="tab in TABS"
          :key="tab"
          class="px-3 py-2 text-sm border-b-2"
          :class="
            activeTab === tab
              ? 'border-n-brand text-n-slate-12'
              : 'border-transparent text-n-slate-11'
          "
          @click="activeTab = tab"
        >
          {{ t(`TABLES.TABS.${tab.toUpperCase()}`) }}
        </button>
      </div>

      <div class="flex-1 p-6 overflow-auto">
        <p v-if="errorMessage" class="p-2 mb-3 text-sm rounded text-n-ruby-11 bg-n-ruby-3">
          {{ errorMessage }}
        </p>

        <template v-if="activeTab === 'rows'">
          <div class="flex flex-wrap items-center gap-2 mb-4">
            <Input
              v-model="search"
              :placeholder="t('TABLES.SEARCH')"
              class="w-64"
              @keyup.enter="runSearch"
            />
            <Button :label="t('TABLES.SEARCH_ACTION')" size="sm" variant="faded" @click="runSearch" />
            <Button
              v-if="canCreateRows"
              :label="t('TABLES.ROW.ADD')"
              size="sm"
              icon="i-lucide-plus"
              @click="addRow"
            />
            <span v-else class="text-sm text-n-slate-11">
              {{ t('TABLES.ROW.NO_CREATE_PERMISSION') }}
            </span>
          </div>

          <p v-if="!columns.length" class="text-sm text-n-slate-11">
            {{ t('TABLES.NO_COLUMNS') }}
          </p>

          <table v-else class="w-full text-sm">
            <thead>
              <tr class="text-left text-n-slate-11">
                <th
                  v-for="column in columns"
                  :key="column.key"
                  class="py-2 pr-3 cursor-pointer select-none whitespace-nowrap"
                  @click="sortColumn(column.key)"
                >
                  {{ column.name }}
                  <span v-if="sortBy === column.key">
                    {{ sortOrder === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th class="py-2" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.id"
                class="border-t cursor-pointer border-n-weak hover:bg-n-solid-2"
                @click="selectedRow = row"
              >
                <td
                  v-for="column in columns"
                  :key="column.key"
                  class="py-2 pr-3 text-n-slate-12"
                >
                  {{ displayValue(row, column) }}
                </td>
                <td class="py-2 text-right">
                  <Button
                    v-if="canDeleteRows"
                    icon="i-lucide-trash-2"
                    size="xs"
                    variant="ghost"
                    color="ruby"
                    @click.stop="removeRow(row)"
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <div class="flex items-center gap-3 mt-4 text-sm text-n-slate-11">
            <Button
              :label="t('TABLES.PREV')"
              size="xs"
              variant="faded"
              :disabled="page <= 1"
              @click="changePage(-1)"
            />
            <span>
              {{ t('TABLES.PAGE_OF', { page, total: tablesStore.totalPages || 1 }) }} ·
              {{ t('TABLES.ROW_COUNT', { count: tablesStore.rowsMeta.total }) }}
            </span>
            <Button
              :label="t('TABLES.NEXT')"
              size="xs"
              variant="faded"
              :disabled="page >= tablesStore.totalPages"
              @click="changePage(1)"
            />
          </div>
        </template>

        <ColumnManager v-else-if="activeTab === 'columns'" :table-id="tableId" :columns="columns" />
        <SavedQueryEditor v-else-if="activeTab === 'queries'" :table-id="tableId" :columns="columns" />
        <GrantManager v-else-if="activeTab === 'grants'" :table-id="tableId" :columns="columns" />
        <CsvImport
          v-else-if="activeTab === 'import'"
          :table-id="tableId"
          :columns="columns"
          @imported="onImported"
        />
      </div>
    </div>

    <RowDetailPanel
      :table-id="tableId"
      :columns="columns"
      :row="selectedRow"
      :writable-keys="writableKeys"
      @close="selectedRow = null"
      @saved="onRowSaved"
    />
  </div>
</template>
